<template>
  <div class="find-vacancy-q-page  container">
    <div
      v-if="pending"
      style="min-height: 20rem; position: relative"
    >
      <BasePreloader/>
    </div>
    <ErrorView
      v-else-if="error"
      :code="error.code"
      :title="error.message"
      class="container-inner"
    />
    <div
      v-else-if="steps"
      class="find-vacancy-q-page__content  container-inner"
    >
      <h1 class="find-vacancy-q-page__title">{{ $tAdditionalQuestionsResponses('answer_few_additional_questions') }}</h1>

      <div class="find-vacancy-q-page__main-content" style="align-items: flex-start;">
        <div class="find-vacancy-q-page__main">
          <BasePreloader v-if="formPending"/>

          <div class="mb-5">
            <div class="d-none d-sm-inline">
              <span>{{ $tVacancy('vacancy') }}: </span>
              <span
                v-if="vacancyFlag"
                :style="`background-image: url('${vacancyFlag}')`"
                style="display: inline-block; min-width: 1.7rem; transform: translateY(0.3rem);"
                class="vacancy-card-small__location-flag ml-1"
              ></span>
              <span class="weight-medium">{{ vacancy[1].name }}</span>
            </div>
          </div>

          <h2>{{ $tVacancy('questions_block_title') }} {{ blockName }}</h2>

          <div
            v-if="steps[currentStep].question_type === 'ONLY_TEXT_OR_IMAGE' && steps[currentStep].question_image"
            class="mb-5"
          >
            <img
              :src="steps[currentStep].question_image"
              alt=""
              style="max-height: 50rem; object-fit: contain; margin: auto"
            />
          </div>

          <h3 class="find-vacancy-q-page__step-title">
            {{ steps[currentStep].question_name }}
          </h3>
          <p
            v-if="showOptionalSubtitle"
            class="find-vacancy-q-page__step-title-note"
          >
            {{ optionalSubtitleText }}
          </p>

          <div class="find-vacancy-q-page__step">
            <div
              v-if="steps[currentStep].question_type === 'QUESTION_CLOSED'"
              class="find-vacancy-q-page__list"
            >
              <BaseRadioList
                v-model="stepsResults[currentStep]"
                :key="steps[currentStep].id"
                :options="steps[currentStep].variants"
                text-key="value"
              >
                <template v-slot:mark>
                  <BaseSvg name="ico-check"/>
                </template>
              </BaseRadioList>
            </div>

            <div v-if="steps[currentStep].question_type === 'QUESTION_OPEN'">
              <BaseInput
                v-model="stepsResults[currentStep]"
                textarea
              />
            </div>

            <div v-if="currentStepWithFilesUploader" class="mb-3">
              <EditFilesDraggable
                :value="stepsResults[currentStep]"
                :max="1"
                :max-caption-length="46"
                :edit-caption-rows-count="3"
                class="additional-questions-responses__edit-files"
                @input="uploadFiles"
                @update="updateFiles"
                @delete="deleteFile"
              />
            </div>
          </div>

          <BaseAlert
            :value="saveError"
            class="find-vacancy-q-page__error  _warning"
          />

          <FindVacancyQPageFooter
            show-save
            :show-back="false"
            :canSave="canSave"
            :save-button-text="$tAdditionalQuestionsResponses('send_replies')"
            class="justify-flex-end"
            @save="onSave"
          />
        </div>

        <div class="find-vacancy-q-page__side items-center d-sm-none">
          <h4 style="text-align: center; margin-top: 0;">
            {{ $tAdditionalQuestionsResponses('you_answer_the_questions') }}<br>{{ $tAdditionalQuestionsResponses('to_vacancy_below') }}
          </h4>
          <VacancyCardSmall
            v-if="vacancy"
            :vacancy="vacancy"
            style="max-width: 32rem"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { serialize } from 'object-to-formdata';
import {http} from '@/axios'
import { mapGetters } from 'vuex';
import ErrorView from '@/components/ErrorView'
import FindVacancyQPageFooter from '@/components/findVacancyQPage/FindVacancyQPageFooter'
import VacancyCardSmall from '@/components/vacancyCardSmall/VacancyCardSmall';
import EditFilesDraggable from '../components/vacancyEdit/EditFilesDraggable'
import handleError from '@/helpers/handleError'
import { EMPLOYEE, PARTNER, AGENT } from '../constants/usersTypes';
import {cloneDeep} from 'lodash';

export default {
  name: 'AdditionalQuestionsResponses',
  components: {
    ErrorView,
    FindVacancyQPageFooter,
    VacancyCardSmall,
    EditFilesDraggable,
  },
  data() {
    return {
      firstLoad: true,

      vacancy: null,

      error: null,
      pending: false,
      paginationBlocks: null, // removed
      blockName: '',
      steps: [],
      currentStep: 0,
      stepsResults: [],

      saveError: null,
      formPending: false,

      haveMoreAdditionalQuestions: true,
    }
  },
  computed: {
    ...mapGetters('user', [
      'userType',
    ]),

    currentStepWithFilesUploader() {
      const availableForTypes = ['QUESTION_OPEN_WITH_FILE', 'UPLOAD_FIRST_PASSPORT', 'UPLOAD_ANOTHER_PASSPORT'];
      return availableForTypes.includes(this.steps[this.currentStep].question_type);
    },
    canSave() {
      const current = this.stepsResults[this.currentStep];
      const filledCurrentStep = Array.isArray(current) ? current.length : current;
      return !!(
        ['ONLY_TEXT_OR_IMAGE', 'UPLOAD_ANOTHER_PASSPORT'].includes(this.steps[this.currentStep].question_type)
        || filledCurrentStep
      );
    },
    showOptionalSubtitle() {
      return ['QUESTION_CLOSED', 'UPLOAD_ANOTHER_PASSPORT'].includes(this.steps[this.currentStep].question_type);
    },
    optionalSubtitleText() {
      if (this.steps[this.currentStep].question_type === 'UPLOAD_ANOTHER_PASSPORT') {
        return `${this.$tAdditionalQuestionsResponses('optional_step')}`;
      }
      return null;
    },
    vacancyFlag() {
      return this.vacancy[3].work_place_country?.flag;
    },
  },
  watch: {
    currentStep() {
      this.initCurrentStep();
    },
  },
  created() {
    this.load();
  },
  mounted() {
    this.$store.dispatch('user/userPusher/createPusher');
    const channel = this.$store.state.user.userPusher.channel;
    channel.bind('update-additional-questions', () => {
      this.load(true)
    });
  },
  beforeDestroy() {
    this.$store.state.user.userPusher.channel?.unbind('update-additional-questions');
  },
  methods: {
    load(forceReload) {
      const pendingKey = (this.firstLoad || forceReload) ? 'pending' : 'formPending';
      this[pendingKey] = true;
      this.stepsResults = [];

      const params = { sent_resume_id: this.$route.params.sentResumeId };

      return http.get('/api/new-vacancy/additional-questions', { params })
        .then(({ data }) => {
          if (data === false) {
            this.haveMoreAdditionalQuestions = false;
            this.$vModal.close('after-respond-without-resume');

            return this.redirectFromPage();
          }

          this.steps = data.questions;
          this.vacancy = data.vacancy;
          this.paginationBlocks = data.blocks;
          this.blockName = data.block_name;
          this.initCurrentStep();
        })
        .finally(() => {
          this[pendingKey] = false;
          this.firstLoad = false;
        });
    },
    redirectFromPage() {
      let redirectRoute = { name: 'vacancies' };

      switch (this.userType) {
        case EMPLOYEE: redirectRoute = { name: 'account-my-vacancies' }; break;
        case PARTNER: redirectRoute = { name: 'account-partner-resumes-base-resumes' }; break;
        case AGENT: redirectRoute = { name: 'account-agent-resumes-base-resumes' }; break;
      }
      this.$router.push(redirectRoute);
    },
    onSave() {
      this.formPending = true;
      this.error = null;
      this.saveError = null;

      const saveData = this.formSaveData();

      http.post('/api/new-vacancy/additional-questions/save', saveData)
        .then(async ({ data }) => {
          const redirectParam = data.data.goto;

          if (redirectParam === 'my-vacancies') {
            await this.$router.push({ name: 'account-my-vacancies' });
          } else if (redirectParam === 'resume-base') {
            await this.redirectFromPage();
          } else if (redirectParam === 'update-page') {
            await this.load();
          }
        })
        .catch(e => this.saveError = handleError(e))
        .finally(() => this.formPending = false);
    },
    formSaveData() {
      const result = this.steps.map((step, index) => {
        const stepResult = cloneDeep(this.stepsResults[index]);
        const onlyTextOrImgData = step.question_type === 'ONLY_TEXT_OR_IMAGE' ? 'Ok' : null;
        const arrayData = Array.isArray(stepResult) ? stepResult.map(this.fileDataObjectToFile) : null;
        const value = onlyTextOrImgData ?? arrayData ?? stepResult;

        return {
          id: step.id,
          question_name: step.question_name,
          question_type: step.question_type,
          sent_resume_id: this.$route.params.sentResumeId,
          value,
        };
      });

      return serialize({ result }, { indices: true });
    },

    initCurrentStep() {
      if (!this.stepsResults[this.currentStep] && this.currentStepWithFilesUploader) {
        this.$set(this.stepsResults, this.currentStep, []);
      }
    },

    uploadFiles(files) {
      const fileData = this.fileToDataObject(files[0], this.stepsResults[this.currentStep]);
      this.stepsResults[this.currentStep].push(fileData);
    },
    updateFiles(files) {
      this.stepsResults[this.currentStep] = files;
    },
    deleteFile(idx) {
      const file = this.stepsResults[this.currentStep][idx]?.file;
      if (file) {
        URL.revokeObjectURL(file);
      }
      this.stepsResults[this.currentStep].splice(idx, 1);
    },

    fileToDataObject(file, bindedList) {
      let id = file.lastModified;
      const link = URL.createObjectURL(file);

      if (bindedList.some(v => v.id === file.lastModified)) {
        id++;
      }

      return {
        vacancy_id: this.vacancy.ID,
        id,
        link,
        path: link,
        title: file.name,
        ext: file.name.split('.').pop(),
        position: bindedList.length,
        file,
      };
    },
    fileDataObjectToFile(dataObject) {
      const isFileDataObject = dataObject?.file instanceof Blob;
      if (isFileDataObject) return { title: dataObject.title, value: dataObject.file };
      return dataObject;
    },
  },
};
</script>
